import {
  Box,
  Button,
  ContentCopyOutlinedIcon,
  CopyToClipboard,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  IconButton,
  List,
  ListItem,
  Text,
} from '@shared/components';

import type { SuccessPublicationResult, ErrorPublicationResult } from '../../types';

import QuestionRefCodeLink from './question-ref-code-link';

interface QSetPublishIncompleteModalProps {
  onDiscard: () => void;
  onAccept: () => void;
  onClose: () => void;
  open: boolean;
  errors: ErrorPublicationResult[];
  successes: SuccessPublicationResult[];
  loading: boolean;
}

export default function QSetPublishIncompleteModal(
  props: Readonly<QSetPublishIncompleteModalProps>,
) {
  const {
    onClose,
    onDiscard,
    onAccept,
    errors,
    successes,
    open: isOpen,
    loading: isLoading,
  } = props;

  const hasSuccessResult = successes.length > 0;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle onClose={onClose}>Publishing Incomplete</DialogTitle>
      <Divider />
      <DialogContent>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2.5) })}>
          <Text>
            พบข้อผิดพลาดในการเผยแพร่ชุดข้อสอบ เนื่องจากข้อสอบมีเนื้อหาที่ยังไม่ได้เผยแพร่
            กรุณาตรวจสอบและแก้ไข ก่อนทำการเผยแพร่ชุดข้อสอบอีกครั้ง
          </Text>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
            <Text variant='body1Semibold'>
              ชุดข้อสอบที่ไม่สามารถเผยแพร่ได้ ({errors.length} ชุด):
            </Text>
            <List
              sx={(theme) => ({
                backgroundColor: theme.palette.error.lighter,
                borderRadius: theme.shape.rounded(2),
                listStyle: 'disc',
                listStylePosition: 'inside',
                p: theme.spacing(4, 3),
              })}
            >
              {errors.map((error) => {
                const { qSetId, details, qSetCode } = error;
                return (
                  <ListItem sx={{ display: 'list-item' }} key={qSetId}>
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      ชุดข้อสอบ: {qSetCode}
                    </Box>
                    <List
                      sx={{
                        listStyle: 'disc',
                        listStylePosition: 'inside',
                        p: (theme) => theme.spacing(1, 0, 0, 3),
                      }}
                    >
                      {details.map((detail) => {
                        const { questionRefCode } = detail;
                        return (
                          <ListItem
                            key={questionRefCode}
                            sx={(theme) => ({
                              display: 'list-item',
                              py: theme.spacing(0),
                              mt: theme.spacing(1),
                            })}
                          >
                            QID: <QuestionRefCodeLink detail={detail} />
                            <CopyToClipboard
                              text={questionRefCode}
                              as={IconButton}
                              size='small'
                              aria-label='Copy QID'
                              sx={{ ml: (theme) => theme.spacing(1) }}
                            >
                              <ContentCopyOutlinedIcon fontSize='inherit' />
                            </CopyToClipboard>
                          </ListItem>
                        );
                      })}
                    </List>
                  </ListItem>
                );
              })}
            </List>
          </Flex>
          {hasSuccessResult ? (
            <>
              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
                <Text variant='body1Semibold'>
                  ชุดข้อสอบที่สามารถเผยแพร่ได้ ({successes.length} ชุด):
                </Text>
                <List
                  sx={(theme) => ({
                    backgroundColor: theme.palette.success.lighter,
                    borderRadius: theme.shape.rounded(2),
                    listStyle: 'disc',
                    listStylePosition: 'inside',
                    p: theme.spacing(4, 3),
                  })}
                >
                  {successes.map((item) => (
                    <ListItem sx={{ display: 'list-item' }} key={item.qSetId}>
                      ชุดข้อสอบ: {item.qSetCode}
                    </ListItem>
                  ))}
                </List>
              </Flex>

              <Text>
                คุณสามารถเลือก <strong>“เผยแพร่เฉพาะชุดข้อสอบที่สมบูรณ์”</strong> หรือ{' '}
                <strong>“ยกเลิกการเผยแพร่ทั้งหมด”</strong>
              </Text>
            </>
          ) : null}
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        {hasSuccessResult ? (
          <>
            <Button color='secondary' variant='outlined' onClick={onDiscard} disabled={isLoading}>
              Cancel All
            </Button>
            <Button onClick={onAccept} loading={isLoading}>
              Publish Complete Sets
            </Button>
          </>
        ) : (
          <Button onClick={onDiscard}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
